import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardHeader, CardContent, TextField, Button,
  Select, MenuItem, FormControl, InputLabel, Alert, Typography,
  Grid, Paper
} from '@mui/material';
import { Email, Send, Info, Settings } from '@mui/icons-material';
import { gmailApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import LoadingButton from '../components/LoadingButton';
import { useModels } from '../hooks/useModels';
import { useUserSettings } from '../hooks/useUserSettings';

const FetchEmails = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { models: AVAILABLE_MODELS, defaultModel } = useModels();
  // Only whitelisted users choose a model; everyone else gets the default
  const { canChooseModels, settings } = useUserSettings();
  const preferredModel = settings?.models?.extraction || defaultModel;
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [formData, setFormData] = useState({
    dateFrom: '',
    dateTo: '',
    categorizationModel: ''
  });

  // Set default model once loaded from API
  useEffect(() => {
    if (canChooseModels && preferredModel && !formData.categorizationModel) {
      setFormData(prev => ({ ...prev, categorizationModel: preferredModel }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canChooseModels, preferredModel]);

  useEffect(() => {
    checkConnection();
    setDefaultDates();
  }, []);

  const checkConnection = async () => {
    try {
      const response = await gmailApi.getStatus();
      setConnected(response.data.connected);
    } catch (error) {
      console.error('Failed to check Gmail connection:', error);
      showError('Failed to check Gmail connection status');
    } finally {
      setCheckingConnection(false);
    }
  };

  const setDefaultDates = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);

    setFormData(prev => ({
      ...prev,
      dateFrom: sevenDaysAgo.toISOString().split('T')[0],
      dateTo: today.toISOString().split('T')[0]
    }));
  };

  const validateDateRange = () => {
    const from = new Date(formData.dateFrom);
    const to = new Date(formData.dateTo);
    const daysDiff = Math.ceil((to - from) / (1000 * 60 * 60 * 24));

    if (daysDiff > 20) {
      showError('Date range cannot exceed 20 days');
      return false;
    }
    if (daysDiff < 0) {
      showError('End date must be after start date');
      return false;
    }
    if (!formData.dateFrom || !formData.dateTo) {
      showError('Please select both start and end dates');
      return false;
    }
    return true;
  };

  const calculateDays = () => {
    if (!formData.dateFrom || !formData.dateTo) return 0;
    const from = new Date(formData.dateFrom);
    const to = new Date(formData.dateTo);
    return Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1;
  };

  const handleSubmit = async () => {
    if (!validateDateRange()) return;

    setLoading(true);
    try {
      const { categorizationModel, ...dates } = formData;
      const response = await gmailApi.process(canChooseModels && categorizationModel ? formData : dates);
      showSuccess(`Email fetching started! Job ID: ${response.data.jobId}`);
      setTimeout(() => navigate('/processing-jobs'), 1500);
    } catch (error) {
      showError(error.response?.data?.error || error.response?.data?.message || 'Failed to start email processing');
    } finally {
      setLoading(false);
    }
  };

  if (checkingConnection) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', p: 3 }}>
        <Typography>Checking Gmail connection...</Typography>
      </Box>
    );
  }

  if (!connected) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Fetch Transaction Emails
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 2 }}>
          Gmail is not connected. To import emails from Gmail, connect it in Settings → Email.
          Forwarding your bank emails (the Forwarded emails tab) works without connecting Gmail.
        </Alert>
        
        <Button 
          variant="contained" 
          startIcon={<Settings />}
          onClick={() => navigate('/settings?tab=email&gmail=1')}
        >
          Go to Settings
        </Button>
      </Box>
    );
  }

  const daysSelected = calculateDays();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Fetch Transaction Emails
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }} icon={<Info />}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          How it works:
        </Typography>
        <Typography variant="body2" component="div">
          1. Fetches emails from your Gmail for the selected date range<br />
          2. AI classifies which emails contain financial transactions<br />
          3. Extracts and categorizes transaction details<br />
          4. Saves them for your review in Processing Jobs
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Maximum date range: 20 days
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader 
              avatar={<Email />}
              title="Email Fetch Settings"
              subheader="Configure date range and processing options"
            />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label="From Date"
                  type="date"
                  value={formData.dateFrom}
                  onChange={(e) => setFormData({ ...formData, dateFrom: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                  helperText="Start date of email fetch"
                />

                <TextField
                  label="To Date"
                  type="date"
                  value={formData.dateTo}
                  onChange={(e) => setFormData({ ...formData, dateTo: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                  helperText="End date is not included - only emails before this date are retrieved"
                />

                {daysSelected > 0 && (
                  <Alert severity={daysSelected > 20 ? 'error' : 'info'}>
                    Selected range: {daysSelected} day{daysSelected !== 1 ? 's' : ''}
                    {daysSelected > 20 && ' - Exceeds maximum of 20 days'}
                  </Alert>
                )}

                {canChooseModels && (
                <FormControl fullWidth>
                  <InputLabel>AI Categorization Model</InputLabel>
                  <Select
                    value={formData.categorizationModel}
                    onChange={(e) => setFormData({ ...formData, categorizationModel: e.target.value })}
                    label="AI Categorization Model"
                  >
                    {AVAILABLE_MODELS.map(model => (
                      <MenuItem key={model.value} value={model.value}>
                        {model.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                )}

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button 
                    variant="outlined" 
                    onClick={() => navigate('/processing-jobs')}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    variant="contained"
                    startIcon={<Send />}
                    onClick={handleSubmit}
                    loading={loading}
                    disabled={daysSelected > 20 || daysSelected <= 0}
                  >
                    Start Fetching Emails
                  </LoadingButton>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, bgcolor: 'background.default' }}>
            <Typography variant="h6" gutterBottom>
              Processing Details
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>Classification:</strong> GPT-5-nano will determine which emails are transaction-related
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>Categorization:</strong> Selected AI model will extract transaction details and categorize them
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>Review:</strong> All transactions will be available in Processing Jobs for your review before saving
            </Typography>
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="caption">
                💡 Tip: For better results, start with a shorter date range to test the categorization quality
              </Typography>
            </Alert>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FetchEmails;

